import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { roadmapApi, RoadmapFull, RoadmapAction, RoadmapHistoryItem } from '../services/roadmapApi';
import { FinancialValue } from '../components/shared/FinancialValue';
import { SupersededAnalysisNote } from '../components/shared/SupersededAnalysisNote';
import {
  Map,
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  DollarSign,
  Shield,
  Target,
  FileText,
  Mail,
  Copy,
  Ban,
  ArrowRight,
  TrendingUp,
  Calendar,
  User,
  History,
  Filter,
  BarChart3,
  Zap
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  'pending': { label: 'Pending', color: 'var(--color-text-muted)', bgColor: 'var(--color-bg-elevated)', icon: Circle },
  'in_progress': { label: 'In Progress', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', icon: Clock },
  'completed': { label: 'Completed', color: 'var(--color-verified)', bgColor: 'transparent', icon: CheckCircle },
  'blocked': { label: 'Blocked', color: 'var(--color-needs-human-text)', bgColor: 'var(--color-needs-human-tint)', icon: AlertTriangle },
  'skipped': { label: 'Skipped', color: 'var(--color-text-dim)', bgColor: 'var(--color-bg-elevated)', icon: Ban }
};

// Difficulty colors
const DIFFICULTY_CONFIG: Record<string, { label: string; color: string }> = {
  'easy': { label: 'Easy', color: 'var(--color-verified)' },
  'moderate': { label: 'Moderate', color: 'var(--color-needs-human-text)' },
  'hard': { label: 'Hard', color: 'var(--color-text-secondary)' }
};

// Action type icons
const ACTION_TYPE_ICONS: Record<string, React.ElementType> = {
  'document': FileText,
  'evidence': FileText,
  'clarification': Mail,
  'verification': CheckCircle,
  'resolution': Target,
  'task': Zap
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export default function StaffRoadmapPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  
  const [roadmap, setRoadmap] = useState<RoadmapFull | null>(null);
  const [history, setHistory] = useState<RoadmapHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedAction, setSelectedAction] = useState<RoadmapAction | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Fetch roadmap data
  const fetchRoadmap = async () => {
    if (!reportId) return;
    
    try {
      setError(null);
      const [roadmapData, historyData] = await Promise.all([
        roadmapApi.getFullRoadmap(reportId),  // Already handles null gracefully
        roadmapApi.getHistory(reportId).catch(() => [])
      ]);
      
      setRoadmap(roadmapData);
      setHistory(historyData);
    } catch (err: any) {
      console.error('Failed to fetch roadmap:', err);
      // Don't show error for "not found" - that's expected for new cases
      if (!err.message?.includes('No roadmap found')) {
        setError(err.message || 'Failed to load roadmap');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoadmap();
  }, [reportId]);

  // Generate roadmap
  const handleGenerate = async (forceRefresh: boolean = false) => {
    if (!reportId) return;
    
    setGenerating(true);
    setError(null);
    try {
      await roadmapApi.generateRoadmap(reportId, forceRefresh);
      await fetchRoadmap();
    } catch (err: any) {
      // Provide helpful error message
      if (err.message?.includes('No agent analyses')) {
        setError('AI analysis required first. Please run the case through the analysis pipeline before generating a roadmap.');
      } else {
        setError(err.message || 'Failed to generate roadmap');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Update action status
  const handleStatusChange = async (actionId: string, newStatus: string) => {
    if (!reportId) return;
    
    try {
      await roadmapApi.updateActionStatus(reportId, actionId, newStatus);
      await fetchRoadmap();
    } catch (err: any) {
      console.error('Failed to update status:', err);
    }
  };

  // Toggle action expansion
  const toggleAction = (actionId: string) => {
    const newExpanded = new Set(expandedActions);
    if (newExpanded.has(actionId)) {
      newExpanded.delete(actionId);
    } else {
      newExpanded.add(actionId);
    }
    setExpandedActions(newExpanded);
  };

  // Guard: actions may be absent or a non-array object from the backend
  const actions = Array.isArray(roadmap?.actions) ? roadmap!.actions : [];

  // Filter actions
  const filteredActions = actions.filter(action => {
    if (statusFilter === 'all') return true;
    return action.status === statusFilter;
  });

  // Calculate totals
  const totalValueUnlock = actions.reduce((sum, a) => sum + (a.value_unlock || 0), 0);
  const totalRiskMitigated = actions.reduce((sum, a) => sum + (a.risk_mitigated || 0), 0);
  const completedValueUnlock = actions
    .filter(a => a.status === 'completed')
    .reduce((sum, a) => sum + (a.value_unlock || 0), 0);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      </AppLayout>
    );
  }

  // The rich detail body reads roadmap.current_state / target_state / progress
  // deeply and unguarded. A degraded payload (roadmap present but a core sub-object
  // absent) would crash the whole page to the error boundary. Gate the detail on
  // those sub-objects being present; otherwise fall through to the unavailable state.
  const roadmapComplete = !!(
    roadmap &&
    roadmap.current_state &&
    roadmap.target_state &&
    roadmap.progress
  );

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Map className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
            <div>
              <h1 className="cs-display">Case Strengthening Roadmap</h1>
              <p className="cs-meta">Report: {reportId}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {roadmap && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg cs-body transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <History className="w-4 h-4" />
                v{roadmap.version}
              </button>
            )}
            <button
              onClick={() => handleGenerate(true)}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg cs-body disabled:opacity-50 transition-colors"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {roadmap ? 'Regenerate' : 'Generate'}
            </button>
          </div>
        </div>

        {error && (
          <div
            className="mb-6 p-4 rounded-lg cs-body"
            style={{
              background: 'var(--color-needs-human-tint)',
              border: '1px solid var(--color-needs-human-tint-bd)',
              color: 'var(--color-needs-human-text)',
            }}
          >
            {error}
          </div>
        )}

        {/* Superseded analysis notice */}
        {roadmap?.figures_currency && (
          <SupersededAnalysisNote currency={roadmap.figures_currency} />
        )}

        {!roadmapComplete ? (
          // No roadmap (or a degraded/incomplete payload) — show generate prompt.
          <div
            className="rounded-xl p-12 text-center"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            <Map className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <h2 className="cs-h2 mb-2">{!roadmap ? 'No Roadmap Generated' : 'Roadmap Data Unavailable'}</h2>
            <p className="cs-meta mb-6">
              {!roadmap
                ? 'Generate a case strengthening roadmap to see actionable steps for improving this case.'
                : 'This roadmap is missing its analysis data. Regenerate it to restore the case-strengthening detail.'}
            </p>
            <button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="px-6 py-3 rounded-lg cs-body disabled:opacity-50 transition-colors"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              {generating ? 'Generating...' : 'Generate Roadmap'}
            </button>
          </div>
        ) : (
          <>
            {/* Version History Sidebar */}
            {showHistory && history.length > 0 && (
              <div
                className="mb-6 p-4 rounded-lg"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
              >
                <h3 className="cs-h3 mb-3">Version History</h3>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      className="p-3 rounded-lg"
                      style={{
                        background: h.is_current ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)',
                        border: `1px solid ${h.is_current ? 'var(--color-needs-human-tint-bd)' : 'var(--color-border-sub)'}`,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>Version {h.version}</span>
                        <span className="cs-meta">{formatDate(h.generated_at)}</span>
                      </div>
                      <div className="cs-meta mt-1">
                        {h.completed_actions}/{h.total_actions} complete ({(h.percent_complete ?? 0).toFixed(0)}%)
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Case Strength */}
              <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  <Target className="w-4 h-4" />
                  <span className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Case Strength</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="cs-figure-lg">
                    {roadmap.current_state.case_strength}
                  </span>
                  <ArrowRight className="w-4 h-4 mb-1" style={{ color: 'var(--color-text-dim)' }} />
                  <span className="cs-figure" style={{ fontSize: '1.25rem', color: 'var(--color-verified)' }}>
                    {roadmap.target_state.achievable_strength}
                  </span>
                </div>
                <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${roadmap.current_state.case_strength}%`, background: 'var(--color-brand)' }}
                  />
                </div>
              </div>

              {/* Progress */}
              <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  <CheckCircle className="w-4 h-4" />
                  <span className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Progress</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="cs-figure-lg">
                    {roadmap.progress.completed_actions}
                  </span>
                  <span className="cs-meta mb-1">/ {roadmap.progress.total_actions} actions</span>
                </div>
                <div className="cs-meta mt-1">
                  {(roadmap.progress.percent_complete ?? 0).toFixed(0)}% complete
                </div>
              </div>

              {/* Value Unlock - STAFF ONLY */}
              <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  <DollarSign className="w-4 h-4" />
                  <span className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Value Unlock</span>
                  <span className="cs-chip px-1.5 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Staff Only</span>
                </div>
                <div className="cs-figure" style={{ fontSize: '1.5rem', fontWeight: 500, color: 'var(--color-verified)' }}>
                  <FinancialValue value={totalValueUnlock} />
                </div>
                <div className="cs-meta mt-1">
                  <FinancialValue value={completedValueUnlock} /> unlocked so far
                </div>
              </div>

              {/* Risk Mitigated - STAFF ONLY */}
              <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  <Shield className="w-4 h-4" />
                  <span className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Risk Mitigated</span>
                  <span className="cs-chip px-1.5 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Staff Only</span>
                </div>
                <div className="cs-figure" style={{ fontSize: '1.5rem', fontWeight: 500, color: 'var(--color-needs-human-text)' }}>
                  <FinancialValue value={totalRiskMitigated} />
                </div>
                <div className="cs-meta mt-1">
                  {roadmap.current_state.vulnerabilities_count} vulnerabilities to address
                </div>
              </div>
            </div>

            {/* Financial Impact Summary - STAFF ONLY */}
            <div className="rounded-xl p-6 mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                <h2 className="cs-h2">Financial Impact Summary</h2>
                <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>STAFF ONLY - Do not share with clients</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="cs-meta">Current Est. Value</div>
                  <div className="cs-figure" style={{ fontSize: '1.25rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    <FinancialValue value={roadmap.current_state.estimated_value} />
                  </div>
                </div>
                <div>
                  <div className="cs-meta">Potential Increase</div>
                  <div className="cs-figure" style={{ fontSize: '1.25rem', fontWeight: 500, color: 'var(--color-verified)' }}>
                    <FinancialValue value={roadmap.target_state.estimated_value_increase} />
                  </div>
                </div>
                <div>
                  <div className="cs-meta">New Est. Value</div>
                  <div className="cs-figure" style={{ fontSize: '1.25rem', fontWeight: 500, color: 'var(--color-needs-human-text)' }}>
                    <FinancialValue value={roadmap.target_state.new_estimated_value} />
                  </div>
                </div>
                <div>
                  <div className="cs-meta">Unclaimed Damages</div>
                  <div className="cs-figure" style={{ fontSize: '1.25rem', fontWeight: 500, color: 'var(--color-needs-human-text)' }}>
                    <FinancialValue value={roadmap.current_state.unclaimed_damages} />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Filter */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="cs-h2">Action Items</h2>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg px-3 py-1.5 cs-body"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
            </div>

            {/* Actions List */}
            <div className="space-y-3">
              {filteredActions.map((action) => {
                const isExpanded = expandedActions.has(action.id);
                const StatusIcon = STATUS_CONFIG[action.status]?.icon || Circle;
                const ActionIcon = ACTION_TYPE_ICONS[action.action_type] || FileText;
                const statusConfig = STATUS_CONFIG[action.status] || STATUS_CONFIG['pending'];
                const difficultyConfig = DIFFICULTY_CONFIG[action.difficulty] || DIFFICULTY_CONFIG['moderate'];
                
                return (
                  <div
                    key={action.id}
                    className="rounded-xl overflow-hidden"
                    style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
                  >
                    {/* Action Header */}
                    <div
                      className="p-4 cursor-pointer transition-colors"
                      onClick={() => toggleAction(action.id)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: statusConfig.bgColor }}>
                            <StatusIcon className="w-4 h-4" style={{ color: statusConfig.color }} />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="cs-body" style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>#{action.priority}</span>
                            <ActionIcon className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                            <span className="cs-chip px-2 py-0.5 rounded" style={{ background: statusConfig.bgColor, color: statusConfig.color }}>
                              {statusConfig.label}
                            </span>
                            {action.is_estimate && (
                              <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                                Estimated
                              </span>
                            )}
                          </div>

                          <h3 className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{action.action}</h3>

                          <div className="flex items-center gap-4 mt-2 cs-meta">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {action.deadline_days} days
                            </span>
                            <span className="flex items-center gap-1" style={{ color: difficultyConfig.color }}>
                              {difficultyConfig.label}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              ~{action.estimated_minutes} min
                            </span>
                          </div>
                        </div>

                        {/* Financial Impact - STAFF ONLY */}
                        <div className="flex-shrink-0 text-right">
                          {action.value_unlock > 0 && (
                            <div className="cs-figure" style={{ fontWeight: 500, color: 'var(--color-verified)' }}>
                              <FinancialValue value={action.value_unlock} />
                            </div>
                          )}
                          {action.risk_mitigated > 0 && (
                            <div className="cs-figure" style={{ color: 'var(--color-needs-human-text)' }}>
                              <FinancialValue value={-action.risk_mitigated} /> risk
                            </div>
                          )}
                          <div className="cs-meta mt-1">
                            +{action.impact_strength_points} pts
                          </div>
                        </div>

                        <div className="flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                          ) : (
                            <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="p-4" style={{ borderTop: '1px solid var(--color-border-sub)', background: 'var(--color-bg-base)' }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Left Column */}
                          <div className="space-y-4">
                            <div>
                              <h4 className="cs-label mb-1" style={{ color: 'var(--color-text-secondary)' }}>Impact Description</h4>
                              <p className="cs-body">{action.impact_description}</p>
                            </div>

                            {action.addresses.length > 0 && (
                              <div>
                                <h4 className="cs-label mb-1" style={{ color: 'var(--color-text-secondary)' }}>Addresses</h4>
                                <ul className="cs-body list-disc list-inside">
                                  {action.addresses.map((addr, i) => (
                                    <li key={i}>{addr}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {action.source_agents.length > 0 && (
                              <div>
                                <h4 className="cs-label mb-1" style={{ color: 'var(--color-text-secondary)' }}>Source Agents</h4>
                                <div className="flex gap-2">
                                  {action.source_agents.map((agent, i) => (
                                    <span key={i} className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
                                      {agent}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right Column */}
                          <div className="space-y-4">
                            <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                              <h4 className="cs-label mb-2" style={{ color: 'var(--color-text-secondary)' }}>Calculation Details</h4>
                              <div className="cs-body space-y-1">
                                <div className="flex justify-between">
                                  <span>Method:</span>
                                  <span className="cs-figure" style={{ fontSize: '0.6875rem' }}>{action.calculation_method || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>ID:</span>
                                  <span className="cs-figure" style={{ fontSize: '0.6875rem' }}>{action.calculation_id || 'N/A'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Dependencies */}
                            {(action.depends_on.length > 0 || action.unlocks.length > 0) && (
                              <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                                <h4 className="cs-label mb-2" style={{ color: 'var(--color-text-secondary)' }}>Dependencies</h4>
                                {action.depends_on.length > 0 && (
                                  <div className="cs-body mb-2">
                                    <span style={{ color: 'var(--color-needs-human-text)' }}>Blocked by:</span> {action.depends_on.length} action(s)
                                  </div>
                                )}
                                {action.unlocks.length > 0 && (
                                  <div className="cs-body">
                                    <span style={{ color: 'var(--color-verified)' }}>Unlocks:</span> {action.unlocks.length} action(s)
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Status Actions */}
                            <div className="flex gap-2">
                              {action.status === 'pending' && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(action.id, 'in_progress');
                                    }}
                                    className="px-3 py-1.5 cs-body rounded"
                                    style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                                  >
                                    Start
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(action.id, 'completed');
                                    }}
                                    className="px-3 py-1.5 cs-body rounded"
                                    style={{ background: 'transparent', color: 'var(--color-verified)' }}
                                  >
                                    Complete
                                  </button>
                                </>
                              )}
                              {action.status === 'in_progress' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(action.id, 'completed');
                                  }}
                                  className="px-3 py-1.5 cs-body rounded"
                                  style={{ background: 'transparent', color: 'var(--color-verified)' }}
                                >
                                  Mark Complete
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAction(action);
                                  setShowEmailModal(true);
                                }}
                                className="px-3 py-1.5 cs-body rounded flex items-center gap-1"
                                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                              >
                                <Mail className="w-3 h-3" />
                                Email
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredActions.length === 0 && (
              <div className="text-center py-12 cs-meta">
                No actions match the current filter.
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
